import React, { useState, useEffect } from 'react';
import { 
  CloudArrowUpIcon, 
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { api } from '../services/api';

const BulkInvoiceManager = ({ onClose }) => {
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [availableInvoices, setAvailableInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  useEffect(() => {
    fetchAvailableInvoices();
  }, []);

  const fetchAvailableInvoices = async () => {
    setLoading(true);
    try {
      const response = await api.invoices.getAll({ 
        status: 'Validated',
        limit: 100 
      });
      setAvailableInvoices(response.data.invoices || []);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleInvoiceSelection = (invoice, isSelected) => {
    if (isSelected) {
      setSelectedInvoices(prev => [...prev, invoice]);
    } else {
      setSelectedInvoices(prev => prev.filter(inv => inv.id !== invoice.id));
    }
  };

  const handleSelectAll = () => {
    if (selectedInvoices.length === availableInvoices.length) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices([...availableInvoices]);
    }
  };

  const handleBulkSubmit = async () => {
    if (selectedInvoices.length === 0) {
      toast.error('Please select at least one invoice');
      return;
    }

    if (selectedInvoices.length > 100) {
      toast.error('Maximum 100 invoices can be submitted at once');
      return;
    }

    setSubmitting(true);
    setBulkResult(null);

    try {
      const invoiceData = selectedInvoices.map(invoice => invoice.json_data);
      const response = await api.invoices.bulkSubmit(invoiceData);
      
      setBulkResult(response.data.bulk_result);
      toast.success(`Bulk submission completed: ${response.data.bulk_result.successful} successful, ${response.data.bulk_result.failed} failed`);
      
      // Refresh available invoices
      await fetchAvailableInvoices();
      setSelectedInvoices([]);
      
    } catch (error) {
      console.error('Bulk submission failed:', error);
      toast.error('Bulk submission failed: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Bulk Invoice Submission</h3>
            <p className="text-sm text-gray-600">Submit multiple validated invoices to FBR at once</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XCircleIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Selection Controls */}
        <div className="mb-6 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleSelectAll}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              {selectedInvoices.length === availableInvoices.length ? 'Deselect All' : 'Select All'}
            </button>
            <span className="text-sm text-gray-600">
              {selectedInvoices.length} of {availableInvoices.length} invoices selected
            </span>
          </div>
          
          <button
            onClick={handleBulkSubmit}
            disabled={selectedInvoices.length === 0 || submitting}
            className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            <CloudArrowUpIcon className="h-5 w-5" />
            <span>{submitting ? 'Submitting...' : 'Submit Selected'}</span>
          </button>
        </div>

        {/* Invoice List */}
        <div className="mb-6">
          <h4 className="text-md font-medium text-gray-900 mb-3">Available Invoices (Validated)</h4>
          
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Select
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Invoice Number
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Buyer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {availableInvoices.map((invoice) => (
                    <tr key={invoice.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedInvoices.some(inv => inv.id === invoice.id)}
                          onChange={(e) => handleInvoiceSelection(invoice, e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {invoice.invoice_number || `INV-${invoice.id}`}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(invoice.invoice_date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {invoice.buyer_business_name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        PKR {(invoice.total_amount || 0).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          {invoice.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {availableInvoices.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No validated invoices available for bulk submission
                </div>
              )}
            </div>
          )}
        </div>

        {/* Bulk Result */}
        {bulkResult && (
          <div className="mb-6">
            <h4 className="text-md font-medium text-gray-900 mb-3">Submission Results</h4>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">{bulkResult.total}</div>
                  <div className="text-sm text-gray-600">Total</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{bulkResult.successful}</div>
                  <div className="text-sm text-gray-600">Successful</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">{bulkResult.failed}</div>
                  <div className="text-sm text-gray-600">Failed</div>
                </div>
              </div>

              {/* Detailed Results */}
              <div className="max-h-48 overflow-y-auto">
                {bulkResult.results.map((result, index) => (
                  <div key={index} className="flex items-center justify-between py-2 border-b border-gray-200 last:border-b-0">
                    <div className="flex items-center space-x-3">
                      {getStatusIcon(result.status)}
                      <span className="text-sm text-gray-900">
                        Invoice {index + 1}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">
                      {result.status === 'success' ? (
                        <span className="text-green-600">
                          FBR: {result.fbr_invoice_number}
                        </span>
                      ) : (
                        <span className="text-red-600">
                          {result.error}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkInvoiceManager;
